"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { isDemoMode } from "@/app/_module/utils/demoMode";
import { SmoothLoadingScreen } from "@/app/_module/components/SmoothLoadingScreen";
import { getSupabaseSession } from "@/app/_module/services/auth";

/**
 * Nothing behind this line mounts without a signed-in user.
 *
 * The app shell (both the desktop and the /m layouts) hangs ~15 providers
 * that each fire an authenticated request on mount — voice token, phone
 * numbers, announcements, subscription, branding, appointments, unread counts…
 * Sessions live in the browser, so the middleware can't tell an agent from a
 * crawler, and the shell used to render for anyone for a beat before the
 * client noticed there was no session. Prod request logs (2026-09-04) showed
 * link-preview crawlers (NAVER, WeChat, Daum, Apple News) opening shared lead
 * URLs and every visit firing ~20 calls that all came back 401.
 *
 * Rules:
 *  - still initializing → render nothing (the loading screen inside the
 *    shell never got a chance to matter here; a blank beat is fine);
 *  - no session → straight to sign-in, with the path to come back to (`next`,
 *    the param the sign-in page already honours);
 *  - profile fetch FAILED but a session exists (transport / pooler blip) →
 *    let the shell mount. That is NOT a dead session, and bouncing it is
 *    exactly the mass-logout we already fixed once;
 *  - demo sandbox → untouched.
 */
export function SessionGate({ children }: { children: React.ReactNode }) {
  const { user, isInitializing, profileLoadFailed } = useUserContext() as any;
  const router = useRouter();
  const pathname = usePathname();

  const demo = isDemoMode();
  const signedIn = !!user?.user?.id;

  // `profileLoadFailed` is ALSO set when there is simply no session (the
  // provider's no-token branch), so it can't tell a crawler from an agent on
  // a bad link by itself. Ask the auth client directly: a live access token
  // with a failed profile is a blip worth waiting out; no token is a bounce.
  const [hasSession, setHasSession] = useState<boolean | null>(null);
  useEffect(() => {
    if (isInitializing || signedIn) return;
    let alive = true;
    getSupabaseSession()
      .then((sess: any) => { if (alive) setHasSession(!!sess?.access_token); })
      .catch(() => { if (alive) setHasSession(false); });
    return () => { alive = false; };
  }, [isInitializing, signedIn, profileLoadFailed]);

  const shouldBounce = !demo && !isInitializing && !signedIn && hasSession === false;
  const blipWithSession = !signedIn && profileLoadFailed && hasSession === true;

  useEffect(() => {
    if (!shouldBounce) return;
    // Give a reasoned sign-out a head start. When a session is ended on
    // purpose — signed in elsewhere, idle timeout, tab closed — SessionValidator
    // signs out and then pushes `/auth/sign-in?reason=…`. This gate sees the
    // token vanish at the same moment; if its own replace() landed second the
    // agent would lose the explanation. Nothing mounts during the wait, so a
    // crawler still fires zero calls — it just waits a beat before leaving.
    const back = pathname && pathname !== "/" ? `?next=${encodeURIComponent(pathname)}` : "";
    const t = setTimeout(() => {
      if (window.location.pathname.startsWith("/auth")) return; // someone else already sent us there
      router.replace(`/auth/sign-in${back}`);
    }, 1500);
    return () => clearTimeout(t);
  }, [shouldBounce, pathname, router]);

  // SmoothLoadingScreen owns the pre-hydration overlay (#__loading-screen,
  // painted by the root layout's inline script) and removes it once the
  // session check settles. It used to live inside the shell; now the shell
  // is behind this gate, so it renders here in every branch — otherwise a
  // bounced visitor would reach sign-in underneath the spinner.
  if (demo) return <>{children}</>;
  if (isInitializing) return <SmoothLoadingScreen />;
  if (!signedIn && !blipWithSession) return <SmoothLoadingScreen />; // checking the token, or bouncing
  return (
    <>
      <SmoothLoadingScreen />
      {children}
    </>
  );
}
